import React, { useEffect, useMemo, useState } from 'react';
import { Drawer } from 'vaul';
import ImageGallery from 'react-image-gallery';
import "react-image-gallery/styles/css/image-gallery.css";
import projectsData from '../data/projectsData';
import { trackEvent } from '../analytics';

const API_BASE = (
    process.env.REACT_APP_API_BASE ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
        ? '/api'
        : '/.netlify/functions')
).replace(/\/+$/, '');

function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
}

function PollSection() {
    const [status, setStatus] = useState('loading');
    const [err, setErr] = useState(null);
    const [options, setOptions] = useState([]);
    const [totalVotes, setTotalVotes] = useState(0);
    const [voteStatus, setVoteStatus] = useState('idle');
    const [votedRowNumber, setVotedRowNumber] = useState(null);
    const [selectedRowNumber, setSelectedRowNumber] = useState(null);

    const sortedOptions = useMemo(() => {
        const copy = Array.isArray(options) ? [...options] : [];
        copy.sort((a, b) => (b?.votes || 0) - (a?.votes || 0));
        return copy;
    }, [options]);

    const maxVotes = useMemo(() => {
        return (options || []).reduce((m, o) => Math.max(m, o?.votes || 0), 0);
    }, [options]);

    useEffect(() => {
        const saved = typeof window !== 'undefined' ? window.localStorage.getItem('poll_voted_row') : null;
        const parsed = Number.parseInt(String(saved ?? ''), 10);
        if (Number.isFinite(parsed) && !Number.isNaN(parsed) && parsed > 0) {
            setVotedRowNumber(parsed);
        }
    }, []);

    async function loadPoll() {
        setErr(null);
        setStatus('loading');
        try {
            const res = await fetch(`${API_BASE}/poll-get`, { method: 'GET', credentials: 'omit' });
            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error('Poll service unavailable.');
            }
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.error || 'Poll service unavailable.');
            }
            setOptions(Array.isArray(data?.options) ? data.options : []);
            setTotalVotes(Number.isFinite(data?.totalVotes) ? data.totalVotes : 0);
            setStatus('ready');
        } catch (e) {
            setErr(e?.message || 'Poll service unavailable.');
            setStatus('error');
        }
    }

    useEffect(() => {
        loadPoll();
    }, []);

    async function onVote(rowNumber) {
        if (voteStatus === 'loading') return;
        if (votedRowNumber) return;
        setErr(null);
        setVoteStatus('loading');
        try {
            const res = await fetch(`${API_BASE}/poll-vote`, {
                method: 'POST',
                credentials: 'omit',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rowNumber })
            });
            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error('Vote service unavailable.');
            }
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.error || 'Vote service unavailable.');
            }
            setOptions(Array.isArray(data?.options) ? data.options : []);
            setTotalVotes(Number.isFinite(data?.totalVotes) ? data.totalVotes : 0);
            const voted = Number.isFinite(data?.votedRowNumber) ? data.votedRowNumber : rowNumber;
            setVotedRowNumber(voted);
            setSelectedRowNumber(null);
            if (typeof window !== 'undefined') {
                window.localStorage.setItem('poll_voted_row', String(voted));
            }
        } catch (e) {
            setErr(e?.message || 'Vote service unavailable.');
        } finally {
            setVoteStatus('idle');
        }
    }

    const canVote = status === 'ready' && !votedRowNumber && voteStatus !== 'loading' && (options?.length || 0) > 0;
    const canSubmitVote = canVote && Number.isFinite(selectedRowNumber) && !Number.isNaN(selectedRowNumber);

    return (
        <div className="mt-12">
            <div className="rounded-2xl border border-slate-100 bg-white/80 backdrop-blur shadow-sm overflow-hidden">
                <div className="p-5 md:p-6">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                            <h3 className="text-xl font-semibold text-slate-900">What should I create next?</h3>
                            <p className="mt-1 text-sm text-slate-600">
                                Vote once to help me prioritize. Results update live.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-600">
                                {totalVotes} vote{totalVotes === 1 ? '' : 's'}
                            </div>
                            <button
                                onClick={loadPoll}
                                className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-md hover:shadow-slate-300/60"
                            >
                                <i className="fa-solid fa-rotate-right text-xs"></i>
                                Refresh
                            </button>
                        </div>
                    </div>

                    {err && (
                        <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                            {err}
                        </div>
                    )}

                    {status === 'loading' && (
                        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                            {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="animate-pulse rounded-xl border border-slate-100 bg-white p-4">
                                    <div className="h-4 w-2/3 rounded bg-slate-100"></div>
                                    <div className="mt-3 h-3 w-full rounded bg-slate-100"></div>
                                    <div className="mt-2 h-3 w-5/6 rounded bg-slate-100"></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {status === 'ready' && !votedRowNumber && (options?.length || 0) > 0 && (
                        <>
                            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                                {(options || []).map((opt) => {
                                    const disabled = !canVote;
                                    const isSelected = selectedRowNumber === opt?.rowNumber;
                                    const cardTone = isSelected ? 'border-slate-900 bg-slate-50' : 'border-slate-100 bg-white';
                                    return (
                                        <button
                                            key={opt?.rowNumber}
                                            onClick={() => setSelectedRowNumber(opt?.rowNumber)}
                                            disabled={disabled}
                                            className={`group relative text-left rounded-xl border px-4 py-3 transition duration-200 ${cardTone} ${
                                                disabled
                                                    ? 'cursor-default opacity-80'
                                                    : 'hover:-translate-y-0.5 hover:shadow-md hover:shadow-slate-200'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold text-slate-900 truncate">{opt?.name || 'Untitled'}</p>
                                                    {!!opt?.description && (
                                                        <p className="mt-1 max-h-10 overflow-hidden text-sm leading-relaxed text-slate-600">{opt.description}</p>
                                                    )}
                                                </div>
                                                {isSelected && (
                                                    <span className="shrink-0 rounded-full bg-slate-900 px-2 py-0.5 text-xs font-semibold text-white">
                                                        Selected
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <p className="text-sm text-slate-600">Click a card to select, then submit your vote.</p>
                                <button
                                    onClick={() => onVote(selectedRowNumber)}
                                    disabled={!canSubmitVote}
                                    className={`inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold text-white transition ${
                                        canSubmitVote
                                            ? 'bg-slate-900 hover:-translate-y-0.5 hover:shadow-md hover:shadow-slate-300/60'
                                            : 'bg-slate-300 cursor-not-allowed'
                                    }`}
                                >
                                    {voteStatus === 'loading' ? 'Submitting…' : 'VOTE'}
                                </button>
                            </div>
                        </>
                    )}

                    {status === 'ready' && !!votedRowNumber && (sortedOptions?.length || 0) > 0 && (
                        <div className="mt-5 space-y-3">
                            {sortedOptions.map((opt, idx) => {
                                const totalPct = totalVotes > 0 ? clamp(Math.round(((opt?.votes || 0) / totalVotes) * 100), 0, 100) : 0;
                                const relativePct = maxVotes > 0 ? clamp(Math.round(((opt?.votes || 0) / maxVotes) * 100), 0, 100) : 0;
                                const isVoted = votedRowNumber === opt?.rowNumber;
                                const cardTone = isVoted ? 'border-teal-200 bg-teal-50' : 'border-slate-100 bg-white';
                                const barTone = isVoted ? 'bg-teal-500' : 'bg-slate-900';
                                return (
                                    <div key={opt?.rowNumber} className={`rounded-xl border p-4 ${cardTone}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs font-semibold text-slate-500">#{idx + 1}</span>
                                                    <p className="text-sm font-semibold text-slate-900 truncate">{opt?.name || 'Untitled'}</p>
                                                    {isVoted && (
                                                        <span className="rounded-full bg-teal-600 px-2 py-0.5 text-xs font-semibold text-white">
                                                            Your vote
                                                        </span>
                                                    )}
                                                </div>
                                                {!!opt?.description && (
                                                    <p className="mt-1 max-h-12 overflow-hidden text-sm leading-relaxed text-slate-600">{opt.description}</p>
                                                )}
                                            </div>
                                            <div className="shrink-0 text-right">
                                                <div className="text-sm font-semibold text-slate-900">{totalPct}%</div>
                                                <div className="text-sm text-slate-600">{opt?.votes || 0} vote{(opt?.votes || 0) === 1 ? '' : 's'}</div>
                                            </div>
                                        </div>
                                        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                            <div className={`h-2 rounded-full ${barTone}`} style={{ width: `${relativePct}%` }}></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {status === 'ready' && (sortedOptions?.length || 0) === 0 && (
                        <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-white/50 p-6 text-center text-slate-500">
                            No poll options found yet.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Projects() {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [expandedAlbum, setExpandedAlbum] = useState(null);
    const allTags = useMemo(() => {
        const tagsSet = new Set();
        projectsData.forEach((project) => project.tags?.forEach((tag) => tagsSet.add(tag)));
        return Array.from(tagsSet).sort();
    }, []);
    const [activeTags, setActiveTags] = useState(() => allTags);

    // Icon mapping for tags
    const tagIcons = {
      healthcare: 'fa-notes-medical',
      design: 'fa-pen-ruler',
      web: 'fa-layer-group',
      ai: 'fa-robot',
      teaching: 'fa-chalkboard-user',
      integral: 'fa-puzzle-piece',
      spiritual: 'fa-book-bible',
      games: 'fa-gamepad',
      game: 'fa-gamepad',
      language: 'fa-language',
      art: 'fa-palette',
      writing: 'fa-feather-pointed',
    };
    const allSelected = activeTags.length === allTags.length;

    const filteredProjects = useMemo(() => {
        if (!activeTags.length) {
            return [];
        }
        if (allSelected) {
            return projectsData;
        }
        return projectsData.filter((project) => project.tags?.some((tag) => activeTags.includes(tag)));
    }, [activeTags, allSelected]);

    const toggleTag = (tag) => {
        setActiveTags((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
        );
    };

    const selectAllTags = () => setActiveTags(allTags);
    const clearAllTags = () => setActiveTags([]);

    const tagLabel = (tag) => {
        const cleanTag = (tag || '').replace(/-/g, ' ');
        const shortUppercase = ['ai', 'ui', 'ux'];
        if (cleanTag === 'design') {
            return 'Web Design';
        }
        if (shortUppercase.includes(cleanTag.toLowerCase())) {
            return cleanTag.toUpperCase();
        }
        return cleanTag.charAt(0).toUpperCase() + cleanTag.slice(1);
    };

    const viewDetails = (project) => {
        trackEvent('project_view', {
            event_category: 'project',
            event_label: project.title,
            link_url: project.link || '',
            site_name: 'hiredalmo'
        });
        setSelectedProject(project);
        setIsOpen(true);
        setExpandedAlbum(null);
    };

    const openProject = (project) => {
        trackEvent('project_click', {
            event_category: 'project',
            event_label: project.title,
            link_url: project.link || '',
            site_name: 'hiredalmo'
        });
        if (project.link) {
            window.open(project.link, '_blank');
        } else {
            viewDetails(project);
        }
    };

    const toggleAlbum = (albumTitle) => {
        setExpandedAlbum(expandedAlbum === albumTitle ? null : albumTitle);
    };

    const getGalleryImages = (album) => {
        return album.images.map(img => ({
            original: `${album.url}/${img}`,
            thumbnail: `${album.url}/${img}`,
            loading: 'eager'
        }));
    };

    return (
        <div className='flex-grow sm mx-auto py-4 px-4'>
          <div className="px-4 py-8">
              <h2 className="text-xl my-8">A curated selection of recent projects.</h2>
              <div className="mb-6 rounded-2xl border border-slate-100 bg-white/80 backdrop-blur shadow-sm">
                <div className="flex flex-wrap gap-3 p-3 md:p-4">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Filter by tag</span>
                  <div className="flex flex-wrap items-center gap-2">
                    {allTags.map((tag) => {
                      const isActive = activeTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          onClick={() => toggleTag(tag)}
                          aria-pressed={isActive}
                          className={`group flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition-all duration-150 ${
                            isActive
                              ? 'bg-slate-900 text-white shadow-md shadow-slate-300/60'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          <i className={`fa-solid ${tagIcons[tag] || 'fa-tag'} text-xs ${isActive ? 'text-white' : 'text-slate-500'}`}></i>
                          <span className="capitalize">{tagLabel(tag)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={clearAllTags}
                      className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200"
                    >
                      <i className="fa-solid fa-circle-xmark text-slate-500"></i>
                      Clear all
                    </button>
                    <button
                      onClick={selectAllTags}
                      className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-md hover:shadow-slate-300/60"
                    >
                      <i className="fa-solid fa-check-double"></i>
                      Select all
                    </button>
                  </div>
                </div>
              </div>

              <div className="container grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredProjects.map((project, idx) => (
                      <div
                        key={`${project.id || 'project'}-${idx}`}
                        data-project-card
                        className="relative flex h-full flex-col overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-100 transition duration-200 hover:-translate-y-0.5 hover:shadow-rose-200 cursor-pointer"
                        onClick={() => openProject(project)}
                      >
                        <div className="flex gap-4 p-4 pb-14">
                          <div className="mx-0 shrink-0 flex-none">
                            <img className="block h-20 w-20 rounded-full object-cover shadow-sm" src={project.icon ? project.icon : '../images/website-design.png'} alt={project.title} />
                          </div>
                          <div className="flex flex-col gap-3 text-left">
                            <p className="text-lg font-semibold text-slate-900">{project.title}</p>
                            <p className="text-sm leading-relaxed text-slate-600">
                                {project.description}
                            </p>
                          </div>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 border-t border-slate-100 bg-slate-50 px-4 py-2">
                          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                              {project.tags && project.tags.slice(0, 6).map((tag, tagIdx) => (
                                  (() => {
                                    const isActiveTag = activeTags.includes(tag);
                                    const bubbleClasses = isActiveTag
                                      ? 'bg-teal-50 ring-teal-100 text-teal-600'
                                      : 'bg-white ring-slate-100 text-slate-600';
                                    const iconClasses = isActiveTag ? 'text-teal-600' : 'text-slate-500';
                                    return (
                                  <span
                                    key={`${project.id || 'project'}-${tag}-${tagIdx}`}
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-full shadow-sm ring-1 ${bubbleClasses}`}
                                    title={tagLabel(tag)}
                                  >
                                    <i className={`fa-solid ${tagIcons[tag] || 'fa-tag'} text-sm ${iconClasses}`}></i>
                                  </span>
                                    );
                                  })()
                              ))}
                              {!project.tags?.length && (
                                <span className="text-xs uppercase tracking-wide text-slate-400">Untagged</span>
                              )}
                          </div>
                        </div>
                      </div>))}
              </div>
              {!filteredProjects.length && (
                <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-white/50 p-6 text-center text-slate-500">
                  No projects match those tags yet.
                </div>
              )}

              <PollSection />
            
          </div>

          {selectedProject && (
              <Drawer.Root open={isOpen} onOpenChange={setIsOpen}>
                  <Drawer.Portal>
                      <Drawer.Overlay onClick={() => setIsOpen(false)} className="bg-black bg-opacity-50 fixed inset-0" />
                      <Drawer.Content className="flex flex-col fixed top-10 bottom-0 lg:left-1/4 lg:right-1/4 bg-white shadow-lg rounded-lg overflow inset-0">
                          <div className='w-full mx-auto flex flex-col items-center p-4 overflow-auto'>
                              <button onClick={() => setIsOpen(false)} className="absolute top-4 right-4 text-3xl">×</button>
                              <img src={selectedProject.icon ? selectedProject.icon : '../images/website-design.png'} alt={selectedProject.title} className="w-32 h-32 rounded-full mb-4" />
                              <h2 className="text-xl font-bold">{selectedProject.title}</h2>
                              <p className="text-md text-slate-500 font-normal mb-4">{selectedProject.story}</p>
                              {selectedProject.links && (
                                  <div className="grid grid-cols-2 gap-4 my-4 w-full">
                                    {selectedProject.links.map(link => (
                                        <a
                                          key={link.title}
                                          href={link.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="bg-teal-500 hover:bg-teal-700 text-white font-bold py-2 px-4 text-center w-full block flex items-center justify-center"
                                        >
                                          {link.title}
                                        </a>
                                      ))}
                                  </div>
                              )}
                              {selectedProject.albums && (
                                  <div className="grid grid-cols-2 gap-4 my-4 w-full">
                                    {selectedProject.albums.map(album => (
                                      <div key={album.title} className="flex flex-col gap-2">
                                        <button className="bg-teal-500 hover:bg-teal-700 text-white font-bold py-2 px-4 w-full block flex items-center justify-center" onClick={() => toggleAlbum(album.title)}>
                                          {album.title}
                                        </button>
                                        {expandedAlbum === album.title && (
                                          <Drawer.Root open={isOpen} onOpenChange={setIsOpen}>
                                            <Drawer.Portal>
                                              <Drawer.Overlay onClick={() => setIsOpen(false)} className="bg-black bg-opacity-75 fixed inset-0" />
                                              <Drawer.Content className="container flex flex-col content-center fixed top-20 bottom-0 left-0 right-0 bg-white shadow-lg rounded-lg overflow inset-0">
                                                <div className='max-h-fit w-full self-center p-4 overflow-auto'>
                                                  <ImageGallery items={getGalleryImages(album)} thumbnailPosition='left' showBullets='true' />
                                                </div>
                                              </Drawer.Content>
                                            </Drawer.Portal>
                                          </Drawer.Root>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                              )}
                          </div>
                      </Drawer.Content>
                  </Drawer.Portal>
              </Drawer.Root>
          )}
        </div>
    );
}

export default Projects;
